import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "react-query";
import {
	fetchNotificationCount,
	fetchNotificationsPaginated,
} from "../queries/notifications.queries";
import type { PaginatedFilter } from "../types/axios.types";
import { markAsRead, markAsUnread } from "../mutations/notifications.mutations";

export const useNotificationsPaginated = (
	preSearch?: string,
	preFilters?: PaginatedFilter,
	preSortBy?: string[],
) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			[
				"notifications",
				page,
				size,
				search || preSearch,
				{ ...preFilters, ...filters },
				sortBy || preSortBy,
			],
			() => {
				try {
					return fetchNotificationsPaginated(
						page,
						size,
						search || preSearch,
						{ ...preFilters, ...filters },
						sortBy || preSortBy,
					);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useNotificationsInfinite = ({
	search,
	filters,
	sortBy,
	size = 10,
}: {
	search?: string;
	filters?: PaginatedFilter;
	sortBy?: string[];
	size?: number;
}) => {
	return useInfiniteQuery({
		queryKey: ["notifications-infinite", search, filters, sortBy, size],
		queryFn: ({ pageParam = 1 }) => {
			return fetchNotificationsPaginated(
				pageParam,
				size,
				search,
				filters,
				sortBy,
			);
		},
		getNextPageParam: (data) => {
			if (!data) return undefined;
			const nextPage = data.meta.currentPage + 1;
			if (data.meta.lastPage <= nextPage) return undefined;
			return nextPage;
		},
	});
};

export const useNotificationCount = () => {
	return useQuery(["notifications-count"], async () => {
		try {
			return fetchNotificationCount();
		} catch (err) {
			return null;
		}
	});
};

export const useMarkAsRead = () => {
	const queryClient = useQueryClient();
	const { mutate } = useMutation(markAsRead, {
		onSettled: () => {
			queryClient.invalidateQueries("notifications");
			queryClient.invalidateQueries("notifications-infinite");
			queryClient.invalidateQueries("notifications-count");
		},
	});
	return mutate;
};

export const useMarkAsUnread = () => {
	const queryClient = useQueryClient();
	const { mutate } = useMutation(markAsUnread, {
		onSettled: () => {
			queryClient.invalidateQueries("notifications");
			queryClient.invalidateQueries("notifications-infinite");
			queryClient.invalidateQueries("notifications-count");
		},
	});
	return mutate;
};
